import React, { useState } from "react";
import _ from "lodash";
import { Box, Button, Text } from "@showitinc/showit-wc";

import ButtonBar from "../base/ButtonBar";
import { queryClient } from "../../utils/QueryUtils";
import SiteActions from "../../actions/SiteActions";
import UserActions from "../../actions/UserActions";
import DesignManager from "../../managers/DesignManager";
import { USER_SITES } from "../../constants/QueryKeys";
import { updateProcessingState } from "../../utils/ProcessingStateUtils";
import styles from "./UserSiteChooserMenu.module.css";

const UserSiteChooserMenu = ({
  site,
  setLoadingState,
  onClose,
  deleteEnabled,
  duplicateEnabled,
  openEnabled,
}) => {
  const [view, setView] = useState("menu");
  const [action, setAction] = useState();

  const _loadSite = (e) => {
    if (DesignManager.needsToSave()) {
      alert(
        "Current site design is still saving.  Please wait until save is complete before loading another design",
      );
    } else {
      updateProcessingState({ processing: true, message: "Loading design...", overlay: true });
      // The loadSite listener in useSites stamps last_access on the cache.
      SiteActions.loadSite(site);
    }
  };

  const _deleteSite = (e) => {
    setAction("delete");
    setView("confirm");
  };

  const _deleteConfirmed = () => {
    const siteInfo = _.pick(site, ["site_design_id", "name"]);

    queryClient.setQueryData([USER_SITES], (prev) => ({
      ...prev,
      sites: prev.sites.filter((cachedSite) => cachedSite.site_design_id !== site.site_design_id),
    }));
    UserActions.deleteSite(site);
    UserActions.trackEvent({ event: "site-delete", meta: siteInfo });
  };

  const _duplicateSite = (e) => {
    setAction("duplicate");
    setView("confirm");
  };

  const _duplicateConfirmed = () => {
    setLoadingState();

    const siteInfo = _.pick(site, ["site_design_id", "name"]);

    const creationInfo = {
      base_design_id: siteInfo.site_design_id,
      name: siteInfo.name + " Copy",
    };

    if (site.folder_id) {
      creationInfo.folder_id = site.folder_id;
    }

    UserActions.copySite(creationInfo);
    UserActions.trackEvent({ event: "site-copy", meta: siteInfo });
  };

  const _close = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose && onClose();
  };

  const _cancelAction = () => {
    setView("menu");
    setAction();
  };

  const _getConfirmView = () => {
    let message = null;
    let btnAction = null;

    switch (action) {
      case "delete":
        message = "Are you sure you want to delete";
        btnAction = _deleteConfirmed;
        break;
      case "duplicate":
        message = "Are you sure you want to duplicate";
        btnAction = _duplicateConfirmed;
        break;
    }

    return (
      <div className={styles.style}>
        <Text variant="header-2" className={styles.confirmMessage}>
          {message}
        </Text>
        <Box pt="3" pb="4">
          <Text variant="header-1" className={styles.confirmMessage}>
            {site.name}
          </Text>
        </Box>
        <ButtonBar style={{ width: "50%" }} align="center">
          <Box mr="5">
            <Button className={styles["cancel-button"]} variant="minimal" onClick={_cancelAction}>
              No
            </Button>
          </Box>
          <Button className={styles["confirm-button"]} variant="feature" onClick={btnAction}>
            Yes
          </Button>
        </ButtonBar>
      </div>
    );
  };

  const _getMenu = () => {
    return (
      <div className={styles["user-site-chooser-menu"]}>
        {deleteEnabled ? (
          <Button
            fullWidth
            variant="destructive"
            iconPrefix="trash"
            onClick={_deleteSite}
            style={{ width: "10rem" }}
          >
            Delete
          </Button>
        ) : null}
        {duplicateEnabled ? (
          <Button
            className={styles.duplicateButton}
            fullWidth
            variant="minimal"
            iconPrefix="galleryAdd"
            onClick={_duplicateSite}
            style={{ width: "10rem" }}
          >
            Duplicate
          </Button>
        ) : null}
        {openEnabled ? (
          <Button
            fullWidth
            variant="feature"
            iconPrefix="openFolderFilled"
            onClick={_loadSite}
            style={{ width: "10rem" }}
          >
            Open
          </Button>
        ) : null}
      </div>
    );
  };

  const viewComponent =
    view === "confirm" ? _getConfirmView() : view === "menu" ? _getMenu() : null;

  return <div className={styles["user-site-chooser-menu-wrapper"]}>{viewComponent}</div>;
};

UserSiteChooserMenu.defaultProps = {
  deleteEnabled: true,
  openEnabled: true,
  duplicateEnabled: true,
};

export default UserSiteChooserMenu;
